import { ampli } from 'lib-shared-tracking';
import { AsyncStatus } from 'lib-shared-types-api';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useCallback, useEffect, useState } from 'react';
import { useMap } from 'react-use';

import { CommandPalettePerformActionFn } from '..';
import {
  CommandPaletteAction,
  CommandPaletteRegisterActionsFn,
  CommandPaletteScope,
} from '../commandPalette.types';
import { CommandPaletteContext } from '../commandPaletteContext';

/** Converts CommandPaletteScope to a string for tracking. */
const getScopeName = (scope: CommandPaletteScope): string => {
  switch (scope) {
    case CommandPaletteScope.User:
      return 'user';
    case CommandPaletteScope.Conversation:
      return 'conversation';
    case CommandPaletteScope.Macro:
      return 'macro';
    default:
      return 'main';
  }
};

type CommandPaletteProviderProps = PropsWithChildren & {
  /** The initial command palette actions. */
  actions?: readonly CommandPaletteAction[];
};

export const CommandPaletteProvider = ({
  actions: initialActions,
  children,
}: CommandPaletteProviderProps): JSX.Element => {
  const [, performedActionsActions] = useMap<
    Record<CommandPaletteAction['id'], string>
  >({});
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('fulfilled');
  const [actions, setActions] = useState<readonly CommandPaletteAction[]>(
    initialActions ?? [],
  );

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [typeAhead, setTypeAhead] = useState('');

  const scope =
    Object.values(CommandPaletteScope).find((scope) => scope === query[0]) ??
    CommandPaletteScope.Main;

  const scopeQuery =
    scope !== CommandPaletteScope.Main ? query.split(scope)[1] : query;

  // Track opened event
  useEffect(() => {
    if (isOpen) {
      ampli.commandPaletteOpened({ openedBy: 'keyboard' });
    }
  }, [isOpen]);

  const registerActions = useCallback<CommandPaletteRegisterActionsFn>(
    (actions) => {
      setActions((currentActions) => currentActions.concat(actions));

      return () => {
        const actionIds = actions.map(({ id }) => id);
        setActions((currentActions) =>
          currentActions.filter(({ id }) => !actionIds.includes(id)),
        );
      };
    },
    [],
  );

  const performAction = useCallback<CommandPalettePerformActionFn>(
    (action) => {
      // Track action selected
      ampli.commandPaletteActionSelected({
        actionLabel: action.label,
        actionSection: action.section,
        commandName: action.commandName,
        query: query || undefined,
        scope: getScopeName(scope),
      });

      // Track closed by action selected (unless keepOpen is true)
      if (!action.keepOpen) {
        ampli.commandPaletteClosed({
          closedBy: 'actionSelected',
          query: query || undefined,
          scope: getScopeName(scope),
        });
      }

      action.performFn?.();

      performedActionsActions.set(action.id, new Date().toISOString());

      // Clear the input after action is performed (unless action wants to keep modal open)
      if (!action.keepOpen) {
        setQuery('');
      }
    },
    [performedActionsActions, query, scope, setQuery],
  );

  return (
    <CommandPaletteContext.Provider
      value={{
        actions,
        asyncStatus,
        isActive,
        isOpen,
        query,
        scope,
        scopeQuery,
        typeAhead,
        performAction,
        registerActions,
        setAsyncStatus,
        setIsActive,
        setIsOpen,
        setQuery,
        setTypeAhead,
      }}
    >
      {children}
    </CommandPaletteContext.Provider>
  );
};
